import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { IEquipment, MeetingRoomCalendarDataAccessRules, TimeFormat } from "types";
import { TenantProvider } from "types";

import { TooltipModule } from "primeng/tooltip";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import moment from "moment";
import { AreaRestrictionIconComponent } from "../area-restriction-icon/area-restriction-icon.component";
import {
  areaHasRestrictedAccess,
  areaHasRestrictedAccessButUserHasAccess,
  areaIsTimeRestricted,
  areaIsTimeRestrictedAndRestrictionTimeHasPassed,
  areaIsTimeRestrictedAndUserHasAccess,
} from "shared/utils/area-restriction-rules";

export interface MeetingRoomInfo {
  capacity: number;
  accessRules: MeetingRoomCalendarDataAccessRules;
  syncProvider: string | null;
  servicesEnabled: {
    isEnabled: boolean;
    categoryNames: { title: string }[];
  };
  equipment: IEquipment[] | [];
}

enum AccessStates {
  priorityAccessButUserHasAccess = "priorityAccessButUserHasAccess",
  priorityAccessButCanBookNow = "priorityAccessButCanBookNow",
  priorityAccessButCantBookBecauseItIsTooEarly = "priorityAccessButCantBookBecauseItIsTooEarly",
  priorityAccessButCantBookBecauseIsNotLookingAtTomorrow = "priorityAccessButCantBookBecauseIsNotLookingAtTomorrow",
  restrictedAccessButUserHasAccess = "restrictedAccessButUserHasAccess",
  restrictedAccessButUserDoesntHaveAccess = "restrictedAccessButUserDoesntHaveAccess",
}

const generateAccessStateTooltipTextsMap = (restrictedTimeString: string): { [key: string]: string } => {
  return {
    [AccessStates.priorityAccessButUserHasAccess]: $localize`:@@booking-module|access-restriction-icon-tooltip|priority-access-user-has-access:You have priority access to book here.\nUsers without priority access can book one day in advance starting at ${restrictedTimeString}.`,
    [AccessStates.priorityAccessButCanBookNow]: $localize`:@@booking-module|access-restriction-icon-tooltip|priority-access-can-book-now:Some users have priority over others to book here.\nYou can book for tomorrow starting at ${restrictedTimeString} today.`,
    [AccessStates.priorityAccessButCantBookBecauseItIsTooEarly]: $localize`:@@booking-module|access-restriction-icon-tooltip|priority-access-can-book-now:Some users have priority over others to book here.\nYou can book for tomorrow starting at ${restrictedTimeString} today.`,
    [AccessStates.priorityAccessButCantBookBecauseIsNotLookingAtTomorrow]: $localize`:@@booking-module|access-restriction-icon-tooltip|priority-access-cant-book-now-future-day:Can't book now.\nYou can only book here one day in advance, starting at ${restrictedTimeString}.`,
    [AccessStates.restrictedAccessButUserHasAccess]: $localize`:@@booking-module|access-restriction-icon-tooltip|restricted-access-user-has-access:You & other authorized colleagues can book here`,
    [AccessStates.restrictedAccessButUserDoesntHaveAccess]: $localize`:@@booking-module|access-restriction-icon-tooltip|restricted-access-user-doesnt-have-access:You can see this area but can't book here`,
  };
};

@Component({
  selector: "db-meeting-room-info-icons",
  templateUrl: "./meeting-room-info-icons.component.html",
  styleUrls: ["./meeting-room-info-icons.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipModule, SvgIconComponent, AreaRestrictionIconComponent],
})
export class MeetingRoomInfoIconsComponent {
  @Input() roomInfo!: MeetingRoomInfo;
  @Input() timeFormat?: TimeFormat;
  @Input() selectedOfficeMomentTimeRange?: moment.Moment;

  // Access rules inputs
  @Input() targetDay?: moment.Moment | Date | string | null;
  @Input() withMargin = true;
  @Input() isTextGrayIcon = false;

  get accessRulesTooltipText(): string {
    return this.generateMeetingRoomTooltipText();
  }

  TenantProvider = TenantProvider;

  getEquipmentTooltip(equipment: IEquipment[]): string {
    return equipment?.length ? equipment.map((item) => item.title).join(", ") : "";
  }

  getServicesTooltip(categoryNames: { title: string }[]): string {
    if (!categoryNames || categoryNames.length === 0) {
      return $localize`:@@meeting-room-info-icons|services-available:Services available`;
    }

    return categoryNames.map((category) => `• ${category.title}`).join("\n");
  }

  get is12HourFormat(): boolean {
    return this.timeFormat === TimeFormat.TWELVE_HOUR;
  }

  get targetDayMoment(): moment.Moment | null {
    if (!this.targetDay) {
      return null;
    }
    return moment.isMoment(this.targetDay) ? this.targetDay : moment(this.targetDay);
  }

  generateMeetingRoomTooltipText(): string {
    if (!this.roomInfo.accessRules || !this.isRestricted(this.roomInfo.accessRules) || !this.targetDayMoment) {
      return "";
    }

    const restrictedTime = this.roomInfo?.accessRules?.restrictedTime || 0;

    const restrictedTimeHoursRaw = Math.floor(restrictedTime / 60 / 60);
    const restrictedTimeMinutes = Math.floor((restrictedTime / 60) % 60) || "00";
    const restrictedTimeAmPm = this.is12HourFormat ? (restrictedTimeHoursRaw >= 12 ? "PM" : "AM") : "";
    const restrictedTimeHours = this.is12HourFormat ? restrictedTimeHoursRaw % 12 || 12 : restrictedTimeHoursRaw;
    const restrictedTimeString = `${restrictedTimeHours}:${restrictedTimeMinutes} ${restrictedTimeAmPm}`;

    const accessStateTooltipTextsMap = generateAccessStateTooltipTextsMap(restrictedTimeString.trim());

    let key: AccessStates | null = null;
    if (areaIsTimeRestricted(this.roomInfo.accessRules)) {
      if (areaIsTimeRestrictedAndUserHasAccess(this.roomInfo.accessRules)) {
        key = AccessStates.priorityAccessButUserHasAccess;
      } else if (areaIsTimeRestrictedAndRestrictionTimeHasPassed(this.roomInfo.accessRules, this.targetDayMoment)) {
        key = AccessStates.priorityAccessButCanBookNow;
      } else {
        const targetDayIsTomorrow = this.targetDayMoment.isSame(moment().add(1, "day"), "day");
        key = targetDayIsTomorrow
          ? AccessStates.priorityAccessButCantBookBecauseItIsTooEarly
          : AccessStates.priorityAccessButCantBookBecauseIsNotLookingAtTomorrow;
      }
    } else if (areaHasRestrictedAccess(this.roomInfo.accessRules)) {
      if (areaHasRestrictedAccessButUserHasAccess(this.roomInfo.accessRules)) {
        key = AccessStates.restrictedAccessButUserHasAccess;
      } else {
        key = AccessStates.restrictedAccessButUserDoesntHaveAccess;
      }
    }

    if (!key) {
      return "";
    }

    const tooltip = accessStateTooltipTextsMap[key];
    return tooltip || "";
  }

  isRestricted = (accessRules: MeetingRoomCalendarDataAccessRules): boolean => {
    return (
      this.roomInfo.accessRules !== undefined &&
      (areaIsTimeRestricted(accessRules) || areaHasRestrictedAccess(accessRules))
    );
  };
}
